import Phaser from 'phaser';

interface Alien {
  sprite: Phaser.GameObjects.Graphics;
  row: number;
  col: number;
  type: 'flagship' | 'red' | 'purple' | 'blue';
  state: 'formation' | 'diving' | 'returning';
  alive: boolean;
  formationX: number;
  formationY: number;
  diveProgress: number;
  divePath: Phaser.Math.Vector2[];
}

interface Star {
  x: number;
  y: number;
  speed: number;
  size: number;
}

export class AlienAttackScene extends Phaser.Scene {
  // Game state
  private score = 0;
  private lives = 3;
  private wave = 1;
  private gameOver = false;
  private paused = false;
  private isAttractMode = false;

  // Game objects
  private player!: Phaser.GameObjects.Graphics;
  private playerX = 0;
  private playerBullet: { x: number; y: number; active: boolean } = {
    x: 0,
    y: 0,
    active: false,
  };
  private playerBulletGraphics!: Phaser.GameObjects.Graphics;

  private aliens: Alien[] = [];
  private enemyBullets: {
    x: number;
    y: number;
    graphics: Phaser.GameObjects.Graphics;
  }[] = [];

  private stars: Star[] = [];
  private starGraphics!: Phaser.GameObjects.Graphics;

  // Formation
  private formationX = 0;
  private formationDirection = 1;
  private formationSpeed = 30;

  // UI
  private scoreText!: Phaser.GameObjects.Text;
  private livesText!: Phaser.GameObjects.Text;
  private startText?: Phaser.GameObjects.Text;

  // Settings
  private readonly PLAYER_SPEED = 250;
  private readonly BULLET_SPEED = 400;
  private readonly ENEMY_BULLET_SPEED = 200;
  private readonly ALIEN_SIZE = 24;
  private readonly FORMATION_TOP = 60;
  private readonly PLAYER_Y = 550;

  // Input
  private cursors?: Phaser.Types.Input.Keyboard.CursorKeys;
  private spaceKey?: Phaser.Input.Keyboard.Key;

  // Callbacks
  private onScoreChange?: (score: number) => void;
  private onGameOver?: (score: number) => void;

  constructor() {
    super({ key: 'AlienAttackScene' });
  }

  init(data: {
    attractMode?: boolean;
    onScoreChange?: (score: number) => void;
    onGameOver?: (score: number) => void;
  }) {
    this.isAttractMode = data.attractMode || false;
    this.onScoreChange = data.onScoreChange;
    this.onGameOver = data.onGameOver;
    this.resetGame();
  }

  private resetGame() {
    this.score = 0;
    this.lives = 3;
    this.wave = 1;
    this.gameOver = false;
    this.paused = false;
    this.aliens = [];
    this.enemyBullets = [];
    this.playerBullet = { x: 0, y: 0, active: false };
    this.formationX = 0;
    this.formationDirection = 1;
  }

  create() {
    const { width, height } = this.scale;

    // Create starfield
    this.createStarfield();

    // Create player
    this.playerX = width / 2;
    this.player = this.add.graphics();
    this.drawPlayer();

    // Create bullet graphics
    this.playerBulletGraphics = this.add.graphics();

    // Create alien formation
    this.createAliens();

    // Create UI
    this.createUI();

    // Input
    if (!this.isAttractMode) {
      this.cursors = this.input.keyboard?.createCursorKeys();
      this.spaceKey = this.input.keyboard?.addKey(
        Phaser.Input.Keyboard.KeyCodes.SPACE
      );

      this.input.keyboard?.on('keydown-P', () => {
        this.paused = !this.paused;
      });

      // Touch controls
      this.input.on('pointerdown', (_pointer: Phaser.Input.Pointer) => {
        if (this.gameOver) {
          this.scene.restart({
            attractMode: false,
            onScoreChange: this.onScoreChange,
            onGameOver: this.onGameOver,
          });
          return;
        }
        // Tap to fire
        this.fire();
      });
    } else {
      // Attract mode - show start text
      this.startText = this.add
        .text(width / 2, height / 2, 'CLICK TO START', {
          fontSize: '32px',
          color: '#ffffff',
          fontFamily: 'monospace',
        })
        .setOrigin(0.5);

      this.tweens.add({
        targets: this.startText,
        alpha: 0.3,
        duration: 800,
        yoyo: true,
        repeat: -1,
      });

      this.input.once('pointerdown', () => {
        this.scene.restart({
          attractMode: false,
          onScoreChange: this.onScoreChange,
          onGameOver: this.onGameOver,
        });
      });
    }
  }

  private createStarfield() {
    const { width, height } = this.scale;
    this.starGraphics = this.add.graphics();

    // Create random stars
    for (let i = 0; i < 50; i++) {
      this.stars.push({
        x: Phaser.Math.Between(0, width),
        y: Phaser.Math.Between(0, height),
        speed: Phaser.Math.Between(20, 60),
        size: Phaser.Math.Between(1, 2),
      });
    }
  }

  private drawPlayer() {
    this.player.clear();
    this.player.fillStyle(0x00ffff, 1);
    // Draw ship shape
    this.player.fillTriangle(
      this.playerX,
      this.PLAYER_Y - 15,
      this.playerX - 15,
      this.PLAYER_Y + 10,
      this.playerX + 15,
      this.PLAYER_Y + 10
    );
    this.player.fillRect(this.playerX - 3, this.PLAYER_Y - 5, 6, 15);
  }

  private createAliens() {
    const { width } = this.scale;
    const startX = width / 2 - (10 * (this.ALIEN_SIZE + 8)) / 2;

    const rows: ('flagship' | 'red' | 'purple' | 'blue')[] = [
      'flagship',
      'red',
      'red',
      'purple',
      'purple',
      'blue',
    ];

    for (let row = 0; row < rows.length; row++) {
      const cols = row === 0 ? 8 : 10; // Fewer flagships
      const offset = row === 0 ? this.ALIEN_SIZE : 0;

      for (let col = 0; col < cols; col++) {
        const x = startX + col * (this.ALIEN_SIZE + 8) + offset;
        const y = this.FORMATION_TOP + row * (this.ALIEN_SIZE + 6);

        const sprite = this.add.graphics();

        const alien: Alien = {
          sprite,
          row,
          col,
          type: rows[row],
          state: 'formation',
          alive: true,
          formationX: x,
          formationY: y,
          diveProgress: 0,
          divePath: [],
        };

        this.drawAlien(alien);
        this.aliens.push(alien);
      }
    }
  }

  private drawAlien(alien: Alien) {
    const x =
      alien.state === 'formation'
        ? alien.formationX + this.formationX
        : alien.divePath.length > 0
          ? alien.divePath[
              Math.min(
                Math.floor(alien.diveProgress),
                alien.divePath.length - 1
              )
            ].x
          : alien.formationX;
    const y =
      alien.state === 'formation'
        ? alien.formationY
        : alien.divePath.length > 0
          ? alien.divePath[
              Math.min(
                Math.floor(alien.diveProgress),
                alien.divePath.length - 1
              )
            ].y
          : alien.formationY;

    alien.sprite.clear();
    if (!alien.alive) return;

    let color: number;
    switch (alien.type) {
      case 'flagship':
        color = 0xffff00;
        break;
      case 'red':
        color = 0xff4444;
        break;
      case 'purple':
        color = 0xff44ff;
        break;
      case 'blue':
        color = 0x4444ff;
        break;
    }

    alien.sprite.fillStyle(color, 1);

    // Draw alien shape based on type
    if (alien.type === 'flagship') {
      // Flagship - larger with wings
      alien.sprite.fillCircle(x, y, 10);
      alien.sprite.fillTriangle(x - 15, y, x - 8, y - 5, x - 8, y + 5);
      alien.sprite.fillTriangle(x + 15, y, x + 8, y - 5, x + 8, y + 5);
      alien.sprite.fillStyle(0xff0000, 1);
      alien.sprite.fillCircle(x, y, 5);
    } else {
      // Regular aliens
      alien.sprite.fillCircle(x, y, 8);
      alien.sprite.fillTriangle(x - 10, y + 5, x, y + 12, x + 10, y + 5);
    }
  }

  private createUI() {
    this.scoreText = this.add.text(10, 10, 'SCORE: 0', {
      fontSize: '20px',
      color: '#ffffff',
      fontFamily: 'monospace',
    });

    const { width } = this.scale;
    this.livesText = this.add
      .text(width - 10, 10, 'LIVES: ♥♥♥', {
        fontSize: '20px',
        color: '#ff4444',
        fontFamily: 'monospace',
      })
      .setOrigin(1, 0);
  }

  private fire() {
    if (this.playerBullet.active || this.gameOver || this.paused) return;

    this.playerBullet = {
      x: this.playerX,
      y: this.PLAYER_Y - 20,
      active: true,
    };
  }

  private addScore(points: number) {
    this.score += points;
    this.scoreText.setText(`SCORE: ${this.score}`);
    this.onScoreChange?.(this.score);
  }

  private loseLife() {
    this.lives--;
    this.livesText.setText('LIVES: ' + '♥'.repeat(Math.max(0, this.lives)));

    if (this.lives <= 0) {
      this.endGame();
    }
  }

  private endGame() {
    if (this.gameOver) return;
    this.gameOver = true;

    const { width, height } = this.scale;

    this.add
      .text(width / 2, height / 2 - 30, 'GAME OVER', {
        fontSize: '48px',
        color: '#ff0000',
        fontFamily: 'monospace',
      })
      .setOrigin(0.5);

    this.add
      .text(width / 2, height / 2 + 30, `FINAL SCORE: ${this.score}`, {
        fontSize: '24px',
        color: '#ffffff',
        fontFamily: 'monospace',
      })
      .setOrigin(0.5);

    this.add
      .text(width / 2, height / 2 + 70, 'CLICK TO RESTART', {
        fontSize: '20px',
        color: '#ffff00',
        fontFamily: 'monospace',
      })
      .setOrigin(0.5);

    this.onGameOver?.(this.score);
  }

  private startDive(alien: Alien) {
    if (alien.state !== 'formation' || !alien.alive) return;

    alien.state = 'diving';
    alien.diveProgress = 0;

    const { width, height } = this.scale;
    const startX = alien.formationX + this.formationX;
    const startY = alien.formationY;

    // Build a multi-phase dive path like original AlienAttack:
    // 1. Circular arc departure from formation
    // 2. Sweeping curve across player position
    // 3. Exit off bottom of screen
    // 4. Loop back up from off-screen and return to formation
    alien.divePath = [];

    const playerTargetX = Phaser.Math.Clamp(
      this.playerX + Phaser.Math.Between(-60, 60),
      30,
      width - 30
    );

    // Determine sweep direction (from which side the alien approaches)
    const sweepDir = startX > width / 2 ? -1 : 1;

    // Phase 1: Circular arc departure (20 steps)
    const arcSteps = 20;
    const arcRadius = 60;
    const arcCenterX = startX + sweepDir * arcRadius;
    const arcCenterY = startY;
    const arcStartAngle = sweepDir > 0 ? Math.PI : 0;
    for (let i = 0; i <= arcSteps; i++) {
      const t = i / arcSteps;
      const angle = arcStartAngle + t * Math.PI * sweepDir;
      const x = arcCenterX + Math.cos(angle) * arcRadius;
      const y = arcCenterY + Math.sin(angle) * arcRadius + t * 80;
      alien.divePath.push(new Phaser.Math.Vector2(x, y));
    }

    // Phase 2: Cubic bezier sweep across player (40 steps)
    const sweepSteps = 40;
    const p0x = alien.divePath[alien.divePath.length - 1].x;
    const p0y = alien.divePath[alien.divePath.length - 1].y;
    const p1x = p0x + sweepDir * 80; // control point 1 - wide approach
    const p1y = p0y + 100;
    const p2x = playerTargetX - sweepDir * 60; // control point 2 - sweep over player
    const p2y = this.PLAYER_Y - 40;
    const p3x = playerTargetX + sweepDir * 120; // end - past player, heading down
    const p3y = this.PLAYER_Y + 30;

    for (let i = 1; i <= sweepSteps; i++) {
      const t = i / sweepSteps;
      const mt = 1 - t;
      const x =
        mt * mt * mt * p0x +
        3 * mt * mt * t * p1x +
        3 * mt * t * t * p2x +
        t * t * t * p3x;
      const y =
        mt * mt * mt * p0y +
        3 * mt * mt * t * p1y +
        3 * mt * t * t * p2y +
        t * t * t * p3y;
      alien.divePath.push(new Phaser.Math.Vector2(x, y));
    }

    // Phase 3: Exit off bottom (15 steps)
    const exitSteps = 15;
    const exitStartX = alien.divePath[alien.divePath.length - 1].x;
    const exitStartY = alien.divePath[alien.divePath.length - 1].y;
    for (let i = 1; i <= exitSteps; i++) {
      const t = i / exitSteps;
      const x = exitStartX + sweepDir * t * 40;
      const y = exitStartY + t * (height - exitStartY + 60);
      alien.divePath.push(new Phaser.Math.Vector2(x, y));
    }

    // Phase 4: Loop back from off-screen to formation (35 steps)
    const returnSteps = 35;
    const loopEntryX = Phaser.Math.Clamp(
      exitStartX + sweepDir * 80,
      -30,
      width + 30
    );
    const loopEntryY = height + 60;
    const r0x = loopEntryX;
    const r0y = loopEntryY;
    const r1x = loopEntryX - sweepDir * 100;
    const r1y = -60;
    const r2x = startX + sweepDir * 50;
    const r2y = -40;
    const r3x = startX;
    const r3y = startY;

    for (let i = 1; i <= returnSteps; i++) {
      const t = i / returnSteps;
      const mt = 1 - t;
      const x =
        mt * mt * mt * r0x +
        3 * mt * mt * t * r1x +
        3 * mt * t * t * r2x +
        t * t * t * r3x;
      const y =
        mt * mt * mt * r0y +
        3 * mt * mt * t * r1y +
        3 * mt * t * t * r2y +
        t * t * t * r3y;
      alien.divePath.push(new Phaser.Math.Vector2(x, y));
    }
  }

  private checkWaveComplete() {
    const aliveAliens = this.aliens.filter((a) => a.alive);
    if (aliveAliens.length === 0) {
      // Next wave
      this.wave++;
      this.formationSpeed += 5;

      // Clear old aliens
      for (const alien of this.aliens) {
        alien.sprite.destroy();
      }
      this.aliens = [];

      // Clear enemy bullets
      for (const bullet of this.enemyBullets) {
        bullet.graphics.destroy();
      }
      this.enemyBullets = [];

      // Create new formation
      this.formationX = 0;
      this.createAliens();

      // Show wave text
      const { width, height } = this.scale;
      const waveText = this.add
        .text(width / 2, height / 2, `WAVE ${this.wave}`, {
          fontSize: '48px',
          color: '#00ff00',
          fontFamily: 'monospace',
        })
        .setOrigin(0.5);

      this.time.delayedCall(1500, () => waveText.destroy());
    }
  }

  update(_time: number, delta: number) {
    if (this.gameOver || this.paused) return;

    const dt = delta / 1000;
    const { width, height } = this.scale;

    // Update starfield
    this.starGraphics.clear();
    for (const star of this.stars) {
      star.y += star.speed * dt;
      if (star.y > height) {
        star.y = 0;
        star.x = Phaser.Math.Between(0, width);
      }
      this.starGraphics.fillStyle(0xffffff, 0.8);
      this.starGraphics.fillCircle(star.x, star.y, star.size);
    }

    // Player input
    if (!this.isAttractMode) {
      if (this.cursors?.left.isDown) {
        this.playerX -= this.PLAYER_SPEED * dt;
      }
      if (this.cursors?.right.isDown) {
        this.playerX += this.PLAYER_SPEED * dt;
      }
      if (this.spaceKey?.isDown) {
        this.fire();
      }

      // Touch movement
      if (this.input.pointer1.isDown) {
        if (this.input.pointer1.x < width / 3) {
          this.playerX -= this.PLAYER_SPEED * dt;
        } else if (this.input.pointer1.x > (width * 2) / 3) {
          this.playerX += this.PLAYER_SPEED * dt;
        }
      }
    } else {
      // Attract mode AI
      const targetAlien = this.aliens.find((a) => a.alive);
      if (targetAlien) {
        const alienX = targetAlien.formationX + this.formationX;
        if (Math.abs(this.playerX - alienX) > 5) {
          this.playerX +=
            (alienX > this.playerX ? 1 : -1) * this.PLAYER_SPEED * dt;
        }
        if (Math.random() < 0.03) this.fire();
      }
    }

    // Clamp player
    this.playerX = Phaser.Math.Clamp(this.playerX, 20, width - 20);
    this.drawPlayer();

    // Update player bullet
    this.playerBulletGraphics.clear();
    if (this.playerBullet.active) {
      this.playerBullet.y -= this.BULLET_SPEED * dt;

      if (this.playerBullet.y < 0) {
        this.playerBullet.active = false;
      } else {
        this.playerBulletGraphics.fillStyle(0xffff00, 1);
        this.playerBulletGraphics.fillRect(
          this.playerBullet.x - 2,
          this.playerBullet.y - 8,
          4,
          16
        );

        // Check collision with aliens
        for (const alien of this.aliens) {
          if (!alien.alive) continue;

          const ax =
            alien.state === 'formation'
              ? alien.formationX + this.formationX
              : alien.divePath[
                  Math.min(
                    Math.floor(alien.diveProgress),
                    alien.divePath.length - 1
                  )
                ]?.x || alien.formationX;
          const ay =
            alien.state === 'formation'
              ? alien.formationY
              : alien.divePath[
                  Math.min(
                    Math.floor(alien.diveProgress),
                    alien.divePath.length - 1
                  )
                ]?.y || alien.formationY;

          const dist = Phaser.Math.Distance.Between(
            this.playerBullet.x,
            this.playerBullet.y,
            ax,
            ay
          );

          if (dist < 15) {
            alien.alive = false;
            alien.sprite.clear();
            this.playerBullet.active = false;

            // Score based on type and state
            let points: number;
            switch (alien.type) {
              case 'flagship':
                points = 60;
                break;
              case 'red':
                points = 40;
                break;
              case 'purple':
                points = 30;
                break;
              case 'blue':
                points = 20;
                break;
            }
            if (alien.state === 'diving') points *= 2;

            this.addScore(points);
            break;
          }
        }
      }
    }

    // Update formation movement
    this.formationX += this.formationDirection * this.formationSpeed * dt;
    if (this.formationX > 80 || this.formationX < -80) {
      this.formationDirection *= -1;
    }

    // Update aliens
    for (const alien of this.aliens) {
      if (!alien.alive) continue;

      if (alien.state === 'formation') {
        // Random chance to start diving
        const diveChance = 0.0005 + this.wave * 0.0001;
        if (Math.random() < diveChance) {
          this.startDive(alien);
        }
      } else if (alien.state === 'diving') {
        alien.diveProgress += 25 * dt * (1 + this.wave * 0.05);

        // Random chance to fire while diving
        if (Math.random() < 0.01) {
          const pos =
            alien.divePath[
              Math.min(
                Math.floor(alien.diveProgress),
                alien.divePath.length - 1
              )
            ];
          if (pos) {
            const bulletGraphics = this.add.graphics();
            this.enemyBullets.push({
              x: pos.x,
              y: pos.y,
              graphics: bulletGraphics,
            });
          }
        }

        if (alien.diveProgress >= alien.divePath.length) {
          // Alien flew off screen - return to formation
          alien.state = 'formation';
          alien.diveProgress = 0;
        }
      }

      this.drawAlien(alien);
    }

    // Update enemy bullets
    for (let i = this.enemyBullets.length - 1; i >= 0; i--) {
      const bullet = this.enemyBullets[i];
      bullet.y += this.ENEMY_BULLET_SPEED * dt;

      bullet.graphics.clear();
      if (bullet.y > height) {
        bullet.graphics.destroy();
        this.enemyBullets.splice(i, 1);
        continue;
      }

      bullet.graphics.fillStyle(0xffffff, 1);
      bullet.graphics.fillCircle(bullet.x, bullet.y, 3);

      // Check collision with player
      const dist = Phaser.Math.Distance.Between(
        bullet.x,
        bullet.y,
        this.playerX,
        this.PLAYER_Y
      );
      if (dist < 20) {
        bullet.graphics.destroy();
        this.enemyBullets.splice(i, 1);
        this.loseLife();
      }
    }

    // Check diving alien collision with player
    for (const alien of this.aliens) {
      if (!alien.alive || alien.state !== 'diving') continue;

      const pos =
        alien.divePath[
          Math.min(Math.floor(alien.diveProgress), alien.divePath.length - 1)
        ];
      if (!pos) continue;

      const dist = Phaser.Math.Distance.Between(
        pos.x,
        pos.y,
        this.playerX,
        this.PLAYER_Y
      );
      if (dist < 25) {
        alien.alive = false;
        alien.sprite.clear();
        this.loseLife();
      }
    }

    // Check wave complete
    this.checkWaveComplete();
  }
}
